import type { RxJsonSchema } from 'rxdb'

/**
 * WorkItemType Document
 *
 * System types (isSystem: true) are seeded by the backend on workspace
 * creation and cannot be modified or deleted by users.  Future custom types
 * will have isSystem: false.
 */
export interface WorkItemTypeDocument {
  id: string
  envId: string
  name: string
  /** Lucide icon name */
  icon: string
  /** Hex color string, e.g. "#ef4444" */
  color: string
  description: string
  sortOrder: string | null
  /** Projects this type is restricted to. Empty = global (visible to all projects). */
  projectIds: Array<string>
  /** System types cannot be modified or deleted */
  isSystem: boolean
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

export const workItemTypeSchema: RxJsonSchema<WorkItemTypeDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    name: {
      type: 'string',
      maxLength: 50,
    },
    icon: {
      type: 'string',
      maxLength: 50,
    },
    color: {
      type: 'string',
      maxLength: 20,
    },
    description: {
      type: 'string',
      maxLength: 200,
    },
    sortOrder: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    projectIds: {
      type: 'array',
      items: {
        type: 'string',
        maxLength: 36,
      },
      default: [],
    },
    isSystem: {
      type: 'boolean',
      default: false,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'name',
    'icon',
    'color',
    'description',
    'isSystem',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    ['envId', 'name'],
    'updatedOn',
    ['envId', 'isDeleted'],
  ],
}

export const workItemTypeMigrationStrategies = {
  // v0 -> v1: backfill `projectIds` (added, array).
  1: (oldDoc: any): any => ({
    ...oldDoc,
    projectIds: Array.isArray(oldDoc.projectIds) ? oldDoc.projectIds : [],
  }),
}
